<template>
  <div class="bg-gray">
    <div class="form-content">
      <el-divider>{{ $T('接送服务') }}</el-divider>
      <p class="price-hint">{{ $T('接送单价说明') }}</p>
      <div class="actions">
        <el-button type="primary" icon="el-icon-plus" @click="addTakeRow">{{ $T('添加接送') }}</el-button>
      </div>

      <el-table :data="takeList" border stripe size="mini" style="width:100%" :empty-text="$T('暂无数据')">
        <el-table-column :label="$T('行程类型')" min-width="120">
          <template slot-scope="scope">
            <el-select v-model="scope.row.direction" :placeholder="$T('请选择行程类型')" clearable style="width:100%">
              <el-option :label="$T('到达')" value="Arrival"></el-option>
              <el-option :label="$T('离港')" value="Departure"></el-option>
            </el-select>
          </template>
        </el-table-column>
        <el-table-column :label="$T('出行日期')" min-width="150">
          <template slot-scope="scope">
            <el-date-picker v-model="scope.row.date" type="date" value-format="yyyy-MM-dd" style="width:100%"></el-date-picker>
          </template>
        </el-table-column>
        <el-table-column :label="$T('航班车次号')" min-width="160">
          <template slot-scope="scope">
            <el-input style="width: 140px;" v-model="scope.row.transcode" :placeholder="$T('请输入航班车次号')"></el-input>
          </template>
        </el-table-column>
        <el-table-column :label="$T('机场站点')" min-width="220">
          <template slot-scope="scope">
            <el-select 
              v-model="scope.row.station_id"
              :placeholder="$T('请选择机场站点')"
              clearable
              style="width:200px"
              @change="onStationChange(scope.row)"
            >
              <el-option
                v-for="station in stationOptions"
                :key="station.id"
                :label="stationOptionLabel(station)"
                :value="station.id"
              ></el-option>
            </el-select>
          </template>
        </el-table-column>
        <el-table-column :label="$T('人数')" min-width="110">
          <template slot-scope="scope">
            <el-input style="width: 80px;" v-model.number="scope.row.member_count" type="number" min="1" @change="normalizeCount(scope.row, 'member_count')"></el-input>
          </template>
        </el-table-column>
        <el-table-column :label="$T('行李件数')" min-width="120">
          <template slot-scope="scope">
            <el-input style="width: 80px;" v-model.number="scope.row.luggage_count" type="number" min="0" @change="normalizeCount(scope.row, 'luggage_count')"></el-input>
          </template>
        </el-table-column>
        <el-table-column :label="$T('状态')" min-width="100">
          <template slot-scope="scope">
            <span>{{ statusLabel(scope.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$T('操作')" width="90" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" style="color:#f56c6c" @click="removeTakeRow(scope.$index)">{{ $T('删除') }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="takePriceItems.length > 0" class="price-breakdown">
        <div v-for="item in takePriceItems" :key="item.station_id" class="price-breakdown__row">
          <span class="price-breakdown__name">{{ item.station_name }}</span>
          <span class="price-breakdown__meta">
            {{ $T('到达合计') }} {{ item.arrival_count }} · {{ $T('离港合计') }} {{ item.departure_count }} ·
            {{ $T('接送计费人数') }} {{ item.billing_count }} × {{ formatMoney(item.unit_price) }} USD
          </span>
          <span class="price-breakdown__line">{{ $T('站点小计') }}: {{ formatMoney(item.line_total) }} USD</span>
        </div>
      </div>

      <div class="summary">
        <span>{{ $T('总价') }}: {{ formatMoney(takeTransTotal) }} USD</span>
      </div>

      <div class="footer-button">
        <el-button type="primary" :loading="saveLoading" @click="onNextStep">{{ $T('下一步') }}</el-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.price-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #909399;
  line-height: 1.5;
}
.actions {
  margin-bottom: 12px;
}
.summary {
  margin-top: 12px;
  font-size: 14px;
  color: #606266;
  display: flex;
  justify-content: flex-end;
  font-weight: 600;
}
.price-breakdown {
  margin-top: 12px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;
}
.price-breakdown__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  padding: 6px 0;
}
.price-breakdown__row + .price-breakdown__row {
  border-top: 1px dashed #dcdfe6;
}
.price-breakdown__name {
  font-weight: 600;
  color: #303133;
  min-width: 120px;
}
.price-breakdown__line {
  margin-left: auto;
  color: #303133;
}
.footer-button {
  margin-top: 16px;
  text-align: center;
}
</style>

<script>
const createTakeRow = () => ({
  direction: null,
  date: '',
  transcode: '',
  station_id: null,
  station_name: '',
  station_address: '',
  member_count: 1,
  luggage_count: 0,
  note: '',
  status: 'Draft',
});

function normalizeTakeDateStr(date) {
  const s = date != null ? String(date).trim() : '';
  if (!s || s === '1970-01-01' || s.indexOf('0000-00-00') === 0) {
    return '';
  }
  return s.length >= 10 ? s.substring(0, 10) : s;
}

function normalizeTakeRowFromApi(row) {
  const r = Object.assign({}, row);
  const rawDir = r.direction != null && String(r.direction).trim() !== '' ? String(r.direction).trim() : '';
  if (rawDir === '') {
    r.direction = null;
  } else {
    const dirLower = rawDir.toLowerCase();
    if (dirLower === 'departure') {
      r.direction = 'Departure';
    } else if (dirLower === 'arrival') {
      r.direction = 'Arrival';
    } else {
      r.direction = null;
    }
  }
  const sid = r.station_id != null && r.station_id !== '' ? Number(r.station_id) : null;
  r.station_id = sid && !Number.isNaN(sid) && sid > 0 ? sid : null;
  r.date = normalizeTakeDateStr(r.date);
  r.transcode = r.transcode != null ? String(r.transcode) : '';
  r.station_name = r.station_name || '';
  r.station_address = r.station_address || '';
  r.member_count = r.member_count != null && r.member_count !== '' ? Number(r.member_count) : 1;
  r.luggage_count = r.luggage_count != null && r.luggage_count !== '' ? Number(r.luggage_count) : 0;
  r.note = r.note || '';
  r.status = r.status || 'Draft';
  return r;
}

function computeTakeTransPriceDetail(takeList, stationOptions) {
  const statsByStation = {};
  (takeList || []).forEach((row) => {
    const sid = row.station_id != null && row.station_id !== '' ? Number(row.station_id) : 0;
    if (!sid || sid <= 0 || !row.direction) {
      return;
    }
    if (!statsByStation[sid]) {
      statsByStation[sid] = { arrival: 0, departure: 0 };
    }
    const count = Math.max(0, parseInt(row.member_count, 10) || 0);
    if (row.direction === 'Arrival') {
      statsByStation[sid].arrival += count;
    } else if (row.direction === 'Departure') {
      statsByStation[sid].departure += count;
    }
  });

  const priceById = {};
  (stationOptions || []).forEach((station) => {
    priceById[Number(station.id)] = Number(station.price) || 0;
  });

  const nameById = {};
  (stationOptions || []).forEach((station) => {
    nameById[Number(station.id)] = station.name || '';
  });

  const items = [];
  let total = 0;
  Object.keys(statsByStation).forEach((sidKey) => {
    const stationId = Number(sidKey);
    const stats = statsByStation[stationId];
    const billingCount = Math.max(stats.arrival, stats.departure);
    if (billingCount < 1) {
      return;
    }
    const unitPrice = priceById[stationId] || 0;
    const lineTotal = Math.round(billingCount * unitPrice * 100) / 100;
    items.push({
      station_id: stationId,
      station_name: nameById[stationId] || '',
      arrival_count: stats.arrival,
      departure_count: stats.departure,
      billing_count: billingCount,
      unit_price: unitPrice,
      line_total: lineTotal,
    });
    total += lineTotal;
  });
  return {
    total: Math.round(total * 100) / 100,
    items,
  };
}

export default {
  name: 'SaiApplyFormStep5',
  data() {
    return {
      saveLoading: false,
      row: {
        id: 0,
      },
      stationOptions: [],
      takeList: [],
    };
  },
  computed: {
    takePriceDetail() {
      return computeTakeTransPriceDetail(this.takeList, this.stationOptions);
    },
    takePriceItems() {
      return this.takePriceDetail.items;
    },
    takeTransTotal() {
      return this.takePriceDetail.total;
    },
  },
  mounted() {
    this.row.id = this.$route.query.id;
    document.title = this.$T('接送服务');
    if (this.row.id) {
      this.fetch();
    }
  },
  methods: {
    formatMoney(value) {
      const n = Number(value);
      return (Number.isNaN(n) ? 0 : n).toFixed(2);
    },
    stationOptionLabel(station) {
      const price = Number(station.price) || 0;
      if (price > 0) {
        return `${station.name} (${this.formatMoney(price)} USD)`;
      }
      return station.name;
    },
    statusLabel(row) {
      const s = row.status;
      if (!s || s === 'Draft') {
        return this.$T('草稿');
      }
      return s;
    },
    fetch() {
      this.$api.post('/api/isai/step5detail', { id: this.row.id }, (res) => {
        const data = res.data || {};
        this.stationOptions = data.station_options || [];
        const list = data.take_service_list || [];
        if (list.length > 0) {
          this.takeList = list.map((item) => normalizeTakeRowFromApi(item));
          this.takeList.forEach((r) => {
            if (r.station_id) {
              this.onStationChange(r);
            }
          });
        } else {
          this.takeList = [createTakeRow()];
        }
      }, (res) => {
        this.$message.error(res.msg || this.$T('加载失败'));
      });
    },
    onStationChange(row) {
      if (row.station_id == null || row.station_id === '' || Number(row.station_id) <= 0) {
        row.station_name = '';
        row.station_address = '';
        return;
      }
      const selected = this.stationOptions.find((item) => Number(item.id) === Number(row.station_id));
      row.station_name = selected ? selected.name : '';
      row.station_address = selected ? selected.address : '';
    },
    normalizeCount(row, field) {
      const value = parseInt(row[field], 10);
      if (field === 'member_count') {
        row[field] = !value || value < 1 ? 1 : value;
        return;
      }
      row[field] = !value || value < 0 ? 0 : value;
    },
    addTakeRow() {
      this.takeList.push(createTakeRow());
    },
    removeTakeRow(index) {
      this.takeList.splice(index, 1);
      if (this.takeList.length === 0) {
        this.takeList.push(createTakeRow());
      }
    },
    onNextStep() {
      for (let i = 0; i < this.takeList.length; i++) {
        const row = this.takeList[i];
        const n = i + 1;
        if (!row.direction || (row.direction !== 'Arrival' && row.direction !== 'Departure')) {
          this.$message.warning(this.$T('请选择行程类型') + ' (' + n + ')');
          return;
        }
        if (!row.date) {
          this.$message.warning(this.$T('请选择出行日期') + ' (' + n + ')');
          return;
        }
        if (!(row.transcode || '').trim()) {
          this.$message.warning(this.$T('请输入航班车次号') + ' (' + n + ')');
          return;
        }
        if (row.station_id == null || row.station_id === '' || Number(row.station_id) <= 0) {
          this.$message.warning(this.$T('请选择机场站点') + ' (' + n + ')');
          return;
        }
      }
      this.saveLoading = true;
      const payload = this.takeList.map((row) => ({
        direction: row.direction,
        date: row.date,
        transcode: (row.transcode || '').trim(),
        station_id: Number(row.station_id),
        member_count: Number(row.member_count || 1),
        luggage_count: Number(row.luggage_count || 0),
        note: row.note || '',
      }));
      this.$api.post(
        '/api/isai/takeSave',
        {
          baoming_id: this.row.id,
          take_list: JSON.stringify(payload),
        },
        () => {
          this.saveLoading = false;
          this.$message.success(this.$T('保存成功'));
          this.fetch();
          rbLink({
            name: 'ISaiApplyDetail',
            query: {
              id: this.row.id,
            },
          }, 'back');
        },
        (res) => {
          this.saveLoading = false;
          this.$message.error(this.$T((res && res.msg) || '保存失败'));
        },
      );
    },
  },
};
</script>



// WEBPACK FOOTER //
// SaiApplyFormStep5.vue?55eed5a6